// Adapter boundary for manuscript review anchors and history-ot comment operations.

import { Comment, CommentList, Range } from "components/organisms/editor/core/model";
import { rangesState, rangesUpdatedEffect, trackedDeletesFromState } from "components/organisms/manuscript/adapter/ot";
import { rangeClickExtension } from "components/organisms/manuscript/codemirror/ranges/interactions";

export function buildReviewAnchor({ selectedNode, selection, editorView }) {
    if (selectedNode?.node_type === "text" && selection?.hasSelection) {
        // Creation-time review anchor persisted by the review API.
        const creationAnchorRange = {
            start_line: selection.startLine,
            start_col: selection.startColumn,
            end_line: selection.endLine,
            end_col: selection.endColumn,
            selected_text: selection.selectedText,
        };

        if (
            editorView &&
            editorView.state.field(rangesState, false) &&
            Number.isFinite(selection.from) &&
            Number.isFinite(selection.to) &&
            selection.from !== selection.to
        ) {
            const from = Math.min(Number(selection.from), Number(selection.to));
            const to = Math.max(Number(selection.from), Number(selection.to));
            const selectedText = selection.selectedText || editorView.state.sliceDoc(from, to);
            creationAnchorRange.op = {
                p: trackedDeletesFromState(editorView.state).toSnapshot(from),
                c: selectedText,
            };
        }

        return {
            anchor_file_id: selectedNode.id,
            anchor_path: selectedNode.path_cache,
            creation_anchor_range: creationAnchorRange,
            description: `${selectedNode.path_cache} · L${selection.startLine}:${selection.startColumn}-L${selection.endLine}:${selection.endColumn}`,
        };
    }
    return null;
}

export function cloneReviewAnchor(anchor) {
    if (!anchor) {
        return null;
    }

    return {
        ...anchor,
        creation_anchor_range: anchor.creation_anchor_range ? { ...anchor.creation_anchor_range } : null,
    };
}

export function resolveReviewAnchor({ anchor = null, selectedNode, selection, editorView }) {
    return cloneReviewAnchor(
        anchor ||
            buildReviewAnchor({
                selectedNode,
                selection,
                editorView,
            }),
    );
}

export function createReviewContextMenuState({
    payload,
    selectedNode,
    editorView,
    menuWidth = 148,
    menuHeight = 44,
    margin = 8,
}) {
    if (selectedNode?.node_type !== "text") {
        return null;
    }

    const selection = payload?.selection;
    if (!selection?.hasSelection) {
        return null;
    }

    return {
        selection,
        anchor: resolveReviewAnchor({
            selectedNode,
            selection,
            editorView,
        }),
        position: {
            top: Math.max(margin, Math.min(window.innerHeight - menuHeight - margin, payload.top)),
            left: Math.max(margin, Math.min(window.innerWidth - menuWidth - margin, payload.left)),
        },
    };
}

export function reviewThreadClickExtension(onSelectReviewThread) {
    if (!onSelectReviewThread) {
        return [];
    }

    return rangeClickExtension((rangeTarget) => {
        if (rangeTarget?.kind !== "comment") {
            return false;
        }

        onSelectReviewThread(rangeTarget.id, { source: "editor", pos: rangeTarget.pos });
        return true;
    });
}

function requireHistoryOtShareDoc(currentDoc) {
    const shareDoc = currentDoc?.historyOTShareDoc;
    if (!shareDoc) {
        throw new Error("history-ot 리뷰 세션을 찾지 못했습니다.");
    }
    return shareDoc;
}

function createReviewComment(threadId, anchorRange, resolved = false) {
    const op = anchorRange?.op;
    if (!op || !Number.isFinite(Number(op.p)) || typeof op.c !== "string" || op.c.length === 0) {
        throw new Error("리뷰 범위의 history-ot 좌표를 찾지 못했습니다.");
    }
    return new Comment(String(threadId), [new Range(Number(op.p), op.c.length)], Boolean(resolved));
}

function cloneComments(snapshot) {
    return CommentList.fromRaw(snapshot?.getComments?.()?.toRaw?.() || []);
}

function syncHistoryOtComments({ currentDoc, editorView, sync }) {
    const shareDoc = requireHistoryOtShareDoc(currentDoc);
    const snapshot = shareDoc?.snapshot;
    if (!snapshot?.getComments) {
        throw new Error("history-ot 리뷰 snapshot 을 찾지 못했습니다.");
    }

    const nextComments = cloneComments(snapshot);
    sync(nextComments);
    snapshot.comments = nextComments;
    shareDoc.emit?.("ranges:dirty");
    shareDoc.emit?.("change");
    shareDoc.emit?.("ranges:update");
    currentDoc?.emit?.("ranges:update");
    currentDoc?.emit?.("ranges:dirty");
    currentDoc?.emit?.("ranges:redraw");
    dispatchRangesUpdated(editorView);
}

function dispatchRangesUpdated(editorView) {
    editorView?.dispatch({
        effects: rangesUpdatedEffect.of(null),
    });
}

export function syncReviewThreadCommentCreated({ currentDoc, editorView, threadId, creationAnchorRange }) {
    syncHistoryOtComments({
        currentDoc,
        editorView,
        sync: (comments) => {
            comments.add(createReviewComment(threadId, creationAnchorRange, false));
        },
    });
}

export function syncReviewThreadCommentResolved({ currentDoc, editorView, threadId, resolved }) {
    syncHistoryOtComments({
        currentDoc,
        editorView,
        sync: (comments) => {
            const comment = comments.getComment(String(threadId));
            if (!comment) {
                throw new Error("현재 history-ot 세션에 리뷰 comment가 없습니다.");
            }
            comments.add(new Comment(comment.id, comment.ranges, Boolean(resolved)));
        },
    });
}

export function toggleReviewThreadSelection(currentThreadId, nextThreadId) {
    const currentId = currentThreadId == null ? null : String(currentThreadId);
    const nextId = nextThreadId == null ? null : String(nextThreadId);

    if (currentId === nextId) {
        return null;
    }
    return nextId;
}

export function createReviewOverlayClosedState() {
    return {
        selectedReviewThreadId: null,
        reviewReplyDraft: "",
        reviewOverlayPosition: null,
    };
}

export function resolveReviewThreadSelection({
    currentThreadId,
    nextThreadId,
    reviewThreads,
    nodeMap,
    getReviewThreadRange,
}) {
    const selectedThreadId = toggleReviewThreadSelection(currentThreadId, nextThreadId);
    if (selectedThreadId == null) {
        return {
            type: "close",
            ...createReviewOverlayClosedState(),
        };
    }

    const thread = reviewThreads.find((item) => String(item.id) === selectedThreadId) || null;
    if (!thread) {
        return {
            type: "noop",
        };
    }

    const nodeId = thread.anchor_file_id;
    const node = nodeId != null ? nodeMap.get(Number(nodeId)) || null : null;
    const range = getReviewThreadRange(thread);

    return {
        type: "select",
        selectedReviewThreadId: selectedThreadId,
        thread,
        node,
        pendingReviewRangeFocus:
            node && range
                ? {
                      nodeId: node.id,
                      line: range.line,
                      startCol: range.startCol,
                      endCol: range.endCol,
                  }
                : null,
    };
}

export function shouldDismissReviewOverlayOnSelectionChange({ selectedThread, selectedNodeId, selection }) {
    if (!selectedThread) {
        return false;
    }

    if (Number(selectedThread.anchor_file_id) !== Number(selectedNodeId)) {
        return true;
    }

    if (!selection) {
        return true;
    }

    if (!selection.hasSelection) {
        return true;
    }

    const creationAnchorRange = selectedThread.creation_anchor_range;
    if (!creationAnchorRange) {
        return true;
    }

    return !(
        Number(selection.startLine) === Number(creationAnchorRange.start_line) &&
        Number(selection.startColumn) === Number(creationAnchorRange.start_col) &&
        Number(selection.endLine) === Number(creationAnchorRange.end_line) &&
        Number(selection.endColumn) === Number(creationAnchorRange.end_col)
    );
}

export function getReviewThreadAnchorRect({
    selectedReviewThread,
    selectedTextNodeId,
    editorView,
    reviewThreadAnchorElementMap,
    getReviewThreadRange,
}) {
    if (!selectedReviewThread) {
        return null;
    }

    if (Number(selectedReviewThread.anchor_file_id) === Number(selectedTextNodeId)) {
        const range = getReviewThreadRange(selectedReviewThread);
        if (range && editorView) {
            const safeLine = Math.min(Math.max(Number(range.line || 1), 1), editorView.state.doc.lines);
            const safeStartCol = Math.max(1, Number(range.startCol || 1));
            const safeEndCol = Math.max(safeStartCol, Number(range.endCol || safeStartCol));

            try {
                const lineInfo = editorView.state.doc.line(safeLine);
                const startPos = Math.min(lineInfo.to, lineInfo.from + Math.max(0, safeStartCol - 1));
                const coords = editorView.coordsAtPos(startPos);
                const endLineInfo = editorView.state.doc.line(safeLine);
                const endPos = Math.min(endLineInfo.to, endLineInfo.from + Math.max(0, safeEndCol - 1));
                const endCoords = editorView.coordsAtPos(endPos);
                if (coords) {
                    return {
                        top: coords.top,
                        bottom: Math.max(coords.bottom, endCoords?.bottom || coords.bottom),
                        left: coords.left,
                        right: Math.max(coords.right, endCoords?.right || coords.right),
                        source: "editor",
                    };
                }
            } catch (_error) {
                // Fall through to the side-panel anchor when the stored range is
                // outside the current document, which can happen in tests or with stale data.
            }
        }
    }

    const threadElement = reviewThreadAnchorElementMap?.get?.(selectedReviewThread.id);
    if (!threadElement) {
        return null;
    }

    const rect = threadElement.getBoundingClientRect();
    return {
        top: rect.top,
        bottom: rect.bottom,
        left: rect.left,
        right: rect.right,
        source: "side",
    };
}

export function buildReviewOverlayPosition({
    anchorRect,
    overlayRect,
    margin = 12,
    defaultWidth = 360,
    defaultHeight = 280,
}) {
    if (!anchorRect) {
        return null;
    }

    const overlayWidth = Math.min(overlayRect?.width || defaultWidth, window.innerWidth - margin * 2);
    const overlayHeight = Math.min(overlayRect?.height || defaultHeight, window.innerHeight - margin * 2);

    if (anchorRect.source === "editor") {
        const fitsBelow = anchorRect.bottom + 12 + overlayHeight <= window.innerHeight - margin;
        const fitsAbove = anchorRect.top - 12 - overlayHeight >= margin;
        const centeredLeft = Math.max(
            margin,
            Math.min(
                window.innerWidth - overlayWidth - margin,
                anchorRect.left + (anchorRect.right - anchorRect.left) / 2 - overlayWidth / 2,
            ),
        );

        if (fitsBelow) {
            return {
                top: anchorRect.bottom + 12,
                left: centeredLeft,
            };
        }

        if (fitsAbove) {
            return {
                top: anchorRect.top - overlayHeight - 12,
                left: centeredLeft,
            };
        }
    }

    const fitsRight = anchorRect.right + 12 + overlayWidth <= window.innerWidth - margin;
    const fitsLeft = anchorRect.left - 12 - overlayWidth >= margin;

    let left;
    if (fitsRight) {
        left = anchorRect.right + 12;
    } else if (fitsLeft) {
        left = anchorRect.left - overlayWidth - 12;
    } else {
        left = Math.max(margin, Math.min(window.innerWidth - overlayWidth - margin, anchorRect.left));
    }

    let top = anchorRect.top;
    if (top + overlayHeight > window.innerHeight - margin) {
        top = Math.max(margin, window.innerHeight - overlayHeight - margin);
    }
    top = Math.max(margin, top);

    return { top, left };
}
